import { MS_IN_HOUR } from '../../../utils/time-utils';
import type { AppRemoteConfig } from '../../platform/Platform';
import type {
  InterstitialSlotName,
  InterstitialStrategy,
  InterstitialStrategyContext,
  InterstitialStrategyState,
} from './InterstitialStrategy';

/**
 * New logic based on remote config
 * Used for Capacitor platform and any platform with remote config loaded
 */
export class RemoteConfigInterstitialStrategy implements InterstitialStrategy {
  constructor(
    private readonly context: InterstitialStrategyContext,
    private readonly state: InterstitialStrategyState,
    private remoteConfig: AppRemoteConfig,
  ) {}

  public updateConfig(config: AppRemoteConfig): void {
    this.remoteConfig = config;
  }

  public canShowInterstitial(slotName: InterstitialSlotName): boolean {
    const {
      ads: { interstitials },
    } = this.remoteConfig;

    if (!interstitials.enabled) return false;
    if (this.isInPayerCooldown()) return false;

    const levelNumber = this.context.getLevelNumber();

    if (interstitials.firstLevel === 0) return false;
    if (levelNumber < interstitials.firstLevel) return false;

    if (interstitials.maxAdsPerSession > 0 && this.state.sessionAdCount >= interstitials.maxAdsPerSession) {
      return false;
    }

    if (this.isGlobalCooldownActive()) return false;

    if (slotName === 'win') {
      if (!interstitials.onWin) return false;
      if (interstitials.winFrequency === 0) return true;

      this.state.slotNameUseCounter.win += 1;
      return this.state.slotNameUseCounter.win % interstitials.winFrequency === 0;
    }

    if (slotName === 'lost') {
      if (!interstitials.onLoss) return false;
      if (interstitials.lossFrequency === 0) return true;

      this.state.slotNameUseCounter.lost += 1;
      return this.state.slotNameUseCounter.lost % interstitials.lossFrequency === 0;
    }

    return false;
  }

  public shouldShowMidLevelInterstitial(): boolean {
    const {
      ads: { interstitials },
    } = this.remoteConfig;

    if (!interstitials.enabled) return false;
    if (!interstitials.midLevelEnabled) return false;
    if (this.isInPayerCooldown()) return false;

    if (interstitials.maxAdsPerSession > 0 && this.state.sessionAdCount >= interstitials.maxAdsPerSession) {
      return false;
    }

    if (this.isGlobalCooldownActive()) return false;

    const now = Date.now();
    const intervalMs = interstitials.everyXSeconds * 1000;
    if (now - this.state.lastInterstitialTimestamp < intervalMs) return false;

    return true;
  }

  public recordInterstitialShown(): void {
    this.state.lastInterstitialTimestamp = Date.now();
    this.state.sessionAdCount += 1;
  }

  private isInPayerCooldown(): boolean {
    const { isPayer, getLastPurchaseTimestamp } = this.context;
    const {
      ads: { interstitials },
    } = this.remoteConfig;

    if (!isPayer()) return false;
    if (interstitials.payerCooldownHours === 0) return false;
    if (!getLastPurchaseTimestamp) return true;

    const lastPurchase = getLastPurchaseTimestamp();
    if (lastPurchase === null) return false;

    const cooldownMs = interstitials.payerCooldownHours * MS_IN_HOUR;
    const now = Date.now();
    return now - lastPurchase < cooldownMs;
  }

  private isGlobalCooldownActive(): boolean {
    const {
      ads: { interstitials },
    } = this.remoteConfig;

    if (interstitials.cooldownSeconds === 0) return false;

    const now = Date.now();
    const cooldownMs = interstitials.cooldownSeconds * 1000;
    return now - this.state.lastInterstitialTimestamp < cooldownMs;
  }
}
